import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchChantiers } from '@/store/thunks/chantierThunks';
import { fetchEngins } from '@/store/thunks/enginThunks';
import { createEnginAffectation, updateEnginAffectation } from '@/store/thunks/enginAffectationThunks';
import type { EnginAffectation } from '@/types';
import type { Chantier } from '@/types';
import type { Engin } from '@/types';
import { Input, Button, Select } from '@/components/ui';

const schema = Yup.object({
  chantierId: Yup.string().required('Chantier obligatoire'),
  engineId: Yup.string().required('Engin obligatoire'),
  date_d: Yup.string().required('Date début obligatoire'),
  date_f: Yup.string(),
});

interface EnginAffectationFormProps {
  affectation?: EnginAffectation | null;
  type: 'create' | 'update';
  onClose: () => void;
  onSuccess?: () => void;
  /** Optional prefill for quick-create flows (e.g. from "non affecté" list) */
  prefill?: {
    chantierId?: number;
    engineId?: number;
    lockChantier?: boolean;
    lockEngine?: boolean;
  };
}

export default function EnginAffectationForm({ affectation, type, onClose, onSuccess, prefill }: EnginAffectationFormProps) {
  const dispatch = useAppDispatch();
  const chantiers = useAppSelector((s: RootState) => s.chantier.chantiers);
  const engins = useAppSelector((s: RootState) => s.engin.engins);

  useEffect(() => {
    dispatch(fetchChantiers());
    dispatch(fetchEngins());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      chantierId: affectation?.chantier?.id
        ? String(affectation.chantier.id)
        : (prefill?.chantierId != null ? String(prefill.chantierId) : ''),
      engineId: affectation?.engine?.id
        ? String(affectation.engine.id)
        : (prefill?.engineId != null ? String(prefill.engineId) : ''),
      date_d: affectation?.date_d ? (affectation.date_d as string).slice(0, 10) : '',
      date_f: affectation?.date_f ? (affectation.date_f as string).slice(0, 10) : '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const chantier = chantiers.find((c: Chantier) => String(c.id) === values.chantierId);
      const engine = engins.find((e: Engin) => String(e.id) === values.engineId);
      if (!chantier || !engine) return;
      const payload = {
        chantier: { id: chantier.id, code: chantier.code },
        engine: { id: engine.id, code: engine.code },
        date_d: values.date_d,
        date_f: values.date_f || undefined,
      };
      if (type === 'update' && affectation?.id) {
        const res = await dispatch(updateEnginAffectation({ ...affectation, ...payload }));
        if (updateEnginAffectation.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await dispatch(createEnginAffectation(payload));
        if (createEnginAffectation.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      }
    },
  });

  const chantierOptions = chantiers.map((c: Chantier) => ({ value: String(c.id), label: c.code ?? c.nom ?? String(c.id) }));
  const engineOptions = engins.map((e: Engin) => ({ value: String(e.id), label: e.code ?? String(e.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <Select
        label="Chantier"
        options={chantierOptions}
        value={formik.values.chantierId}
        disabled={Boolean(prefill?.lockChantier) && type === 'create'}
        onChange={(e) => formik.setFieldValue('chantierId', e.target.value)}
        onBlur={formik.handleBlur}
        error={formik.touched.chantierId && formik.errors.chantierId ? String(formik.errors.chantierId) : undefined}
      />
      <Select
        label="Engin"
        options={engineOptions}
        value={formik.values.engineId}
        disabled={Boolean(prefill?.lockEngine) && type === 'create'}
        onChange={(e) => formik.setFieldValue('engineId', e.target.value)}
        onBlur={formik.handleBlur}
        error={formik.touched.engineId && formik.errors.engineId ? String(formik.errors.engineId) : undefined}
      />
      <Input
        label="Date début"
        type="date"
        id="date_d"
        {...formik.getFieldProps('date_d')}
        error={formik.touched.date_d && formik.errors.date_d ? String(formik.errors.date_d) : undefined}
      />
      <Input
        label="Date fin"
        type="date"
        id="date_f"
        {...formik.getFieldProps('date_f')}
      />
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>{type === 'update' ? 'Modifier' : 'Créer'}</Button>
      </div>
    </form>
  );
}
